import generateHTML from '../../publishing/generateHTML';
import { generateFontCSS } from '../../utils/fontHelper';
import { buildApiUrl } from '../../config/api';

const DEVICES = {
  DESKTOP: 'desktop',
  TABLET: 'tablet',
  MOBILE: 'mobile'
};

// Function to fetch content for template pages
export const fetchTemplateContent = async (contentTypeId, siteId) => {
  try {
    const response = await fetch(buildApiUrl(`api/cms/content/${siteId}/${contentTypeId}`));
    const data = await response.json();
    return data.success ? data.content : [];
  } catch (error) {
    console.error('Error fetching template content:', error);
    return [];
  }
};

// Function to fetch content for CMS elements
const fetchElementContent = async (contentTypeId, siteId) => {
  try {
    const response = await fetch(buildApiUrl(`api/cms/content/${siteId}/${contentTypeId}`));
    const data = await response.json();
    return data.success ? data.content : [];
  } catch (error) {
    console.error('Error fetching element content:', error);
    return [];
  }
};

// Helper function to find CMS elements in a page
const findCMSElements = (elements) => {
  const cmsElements = [];

  const traverse = (element) => {
    if (element.type === 'cms-content') {
      cmsElements.push(element);
    }
    element.children?.forEach(traverse);
  };

  elements.forEach(traverse);
  return cmsElements;
};

// Add cart functionality
const cartJS = `
  // Cart data structure
  let cart = {
    items: [],
    total: 0
  };
  
  // Template for cart items (will be populated from the test product)
  let cartItemTemplate = null;
  
  // Function to initialize the cart item template from the test product
  function initializeCartItemTemplate() {
    // Find the test product in the DOM
    const testProduct = document.querySelector('[data-element="cart-item"][data-product-id="Test-Product-1"]');
    if (!testProduct) {
      console.warn('Test product not found for template');
      return null;
    }
    
    console.log('Found test product for template:', testProduct);
    
    // Clone the test product to use as a template
    cartItemTemplate = testProduct.cloneNode(true);
    
    // Reset display style to ensure it's visible when used
    cartItemTemplate.style.display = 'flex';
    
    return cartItemTemplate;
  }
  
  // Function to create a new cart item from the template
  function createCartItemFromTemplate(product) {
    if (!cartItemTemplate) {
      cartItemTemplate = initializeCartItemTemplate();
      if (!cartItemTemplate) {
        console.error('Failed to initialize cart item template');
        return null;
      }
    }
    
    // Clone the template
    const cartItem = cartItemTemplate.cloneNode(true);
    
    // Ensure price is a valid number with 2 decimal places
    const price = parseFloat(product.price).toFixed(2);
    
    // Update product-specific attributes
    cartItem.setAttribute('data-product-id', product.id);
    cartItem.setAttribute('data-price', price);
    cartItem.setAttribute('data-quantity', product.quantity);
    
    // Update product name
    const productName = cartItem.querySelector('[data-element="product-name"]') || 
                        cartItem.querySelector('h3, h4') || 
                        cartItem.querySelector('.cart-product-name');
    if (productName) {
      productName.textContent = product.name;
    }
    
    // Update product image
    const productImage = cartItem.querySelector('img');
    if (productImage && product.image) {
      productImage.src = product.image;
      productImage.alt = product.name;
    }
    
    // Update product price
    const productPrice = cartItem.querySelector('[data-element="product-price"]');
    if (productPrice) {
      productPrice.textContent = \`$\${price}\`;
      productPrice.setAttribute('data-price', price);
    }
    
    // Update quantity value
    const quantityValue = cartItem.querySelector('[data-element="quantity-value"]');
    if (quantityValue) {
      quantityValue.textContent = product.quantity;
      quantityValue.setAttribute('data-quantity', product.quantity);
    }
    
    // Update quantity container
    const quantityContainer = cartItem.querySelector('[data-element="product-quantity"]');
    if (quantityContainer) {
      quantityContainer.setAttribute('data-quantity', product.quantity);
    }
    
    // Add event listeners to buttons
    const minusBtn = cartItem.querySelector('.quantity-btn-minus') || cartItem.querySelector('button:first-of-type');
    const plusBtn = cartItem.querySelector('.quantity-btn-plus') || cartItem.querySelector('button:last-of-type');
    const removeBtn = cartItem.querySelector('[data-element="remove-button"]') || cartItem.querySelector('button:last-of-type');
    
    if (minusBtn) {
      minusBtn.onclick = function(e) {
        e.preventDefault();
        e.stopPropagation();
        updateQuantity(product.id, product.quantity - 1);
      };
    }
    
    if (plusBtn) {
      plusBtn.onclick = function(e) {
        e.preventDefault();
        e.stopPropagation();
        updateQuantity(product.id, product.quantity + 1);
      };
    }
    
    if (removeBtn) {
      removeBtn.onclick = function(e) {
        e.preventDefault();
        e.stopPropagation();
        removeFromCart(product.id);
      };
    }
    
    return cartItem;
  }
  
  // Load cart from localStorage on page load
  function loadCart() {
    const savedCart = localStorage.getItem('websiteBuilderPublishedCart');
    if (savedCart) {
      try {
        cart = JSON.parse(savedCart);
      } catch (e) {
        console.error('Error parsing cart data:', e);
        cart = { items: [], total: 0 };
      }
    }
    updateCartDisplay();
  }
  
  // Save cart to localStorage
  function saveCart() {
    localStorage.setItem('websiteBuilderPublishedCart', JSON.stringify(cart));
  }
  
  // Add item to cart
  function addToCart(product) {
    console.log('Adding to cart:', product);
    
    // Ensure price is a valid number
    const price = parseFloat(product.price) || 0;
    
    // Check if item already exists in cart
    const existingItem = cart.items.find(item => item.id === product.id);
    
    if (existingItem) {
      // Increment quantity if item exists
      existingItem.quantity += 1;
    } else {
      // Add new item with quantity 1
      cart.items.push({
        ...product,
        price: price.toFixed(2), // Ensure price is stored with 2 decimal places
        quantity: 1
      });
    }
    
    // Update cart total
    updateCartTotal();
    
    // Save cart to localStorage
    saveCart();
    
    // Update cart display
    updateCartDisplay();
    
    // Show the cart after adding an item
    showCart();
  }
  
  // Remove item from cart
  function removeFromCart(productId) {
    console.log('Removing from cart:', productId);
    
    // Filter out the item to remove
    cart.items = cart.items.filter(item => item.id !== productId);
    
    // Update cart total
    updateCartTotal();
    
    // Save cart to localStorage
    saveCart();
    
    // Update cart display
    updateCartDisplay();
  }
  
  // Update item quantity
  function updateQuantity(productId, newQuantity) {
    console.log('Updating quantity:', productId, newQuantity);
    
    // Find the item to update
    const item = cart.items.find(item => item.id === productId);
    
    if (item) {
      if (newQuantity <= 0) {
        // Remove item if quantity is 0 or less
        removeFromCart(productId);
      } else {
        // Update quantity
        item.quantity = newQuantity;
        
        // Update cart total
        updateCartTotal();
        
        // Save cart to localStorage
        saveCart();
        
        // Update cart display
        updateCartDisplay();
      }
    }
  }
  
  // Update cart total
  function updateCartTotal() {
    cart.total = cart.items.reduce((total, item) => {
      return total + (parseFloat(item.price) * item.quantity);
    }, 0);
  }
  
  // Show cart
  function showCart() {
    const cartContainer = document.querySelector('[data-element="cart-container"]');
    const cartWrapper = document.querySelector('[data-element="cart-wrapper"]');
    if (cartContainer && cartWrapper) {
      cartContainer.style.display = 'flex';
      cartWrapper.style.display = 'flex';
      console.log('Cart shown');
      
      // Update ARIA attributes
      cartContainer.setAttribute('aria-hidden', 'false');
      cartWrapper.setAttribute('aria-hidden', 'false');
      
      // Update cart button aria-expanded attribute
      const cartButton = document.querySelector('[data-element="cart-button"]');
      if (cartButton) {
        cartButton.setAttribute('aria-expanded', 'true');
      }
    }
  }
  
  // Hide cart
  function hideCart() {
    const cartContainer = document.querySelector('[data-element="cart-container"]');
    const cartWrapper = document.querySelector('[data-element="cart-wrapper"]');
    if (cartContainer && cartWrapper) {
      cartContainer.style.display = 'none';
      cartWrapper.style.display = 'none';
      console.log('Cart hidden');
      
      // Update ARIA attributes
      cartContainer.setAttribute('aria-hidden', 'true');
      cartWrapper.setAttribute('aria-hidden', 'true');
      
      // Update cart button aria-expanded attribute
      const cartButton = document.querySelector('[data-element="cart-button"]');
      if (cartButton) {
        cartButton.setAttribute('aria-expanded', 'false');
      }
    }
  }
  
  // Toggle cart visibility
  function toggleCart() {
    console.log('Toggle cart called');
    const cartContainer = document.querySelector('[data-element="cart-container"]');
    if (cartContainer) {
      // Get the current computed style to check visibility
      const computedStyle = window.getComputedStyle(cartContainer);
      const isVisible = computedStyle.display !== 'none';
      
      console.log('Current cart visibility:', isVisible ? 'visible' : 'hidden');
      
      // Toggle visibility
      if (isVisible) {
        hideCart();
      } else {
        // Load the latest cart data from localStorage before showing the cart
        loadCart();
        showCart();
      }
    } else {
      console.error('Cart container element not found');
    }
  }
  
  // Update cart display
  function updateCartDisplay() {
    // Update cart button count
    const cartButton = document.querySelector('[data-element="cart-button"]');
    if (cartButton) {
      const itemCount = cart.items.reduce((count, item) => count + item.quantity, 0);
      cartButton.setAttribute('data-count', itemCount);
      cartButton.textContent = \`Cart (\${itemCount})\`;
    }
    
    // Update cart content
    const cartContent = document.querySelector('[data-element="my-cart-content"]');
    if (cartContent) {
      console.log('Found cart content element:', cartContent);
      
      // Find empty state container
      const emptyState = cartContent.querySelector('[data-element="empty-state"]');
      
      if (cart.items.length === 0) {
        // Show empty state container
        if (emptyState) {
          emptyState.style.display = 'flex';
          console.log('Showing empty state container');
        } else {
          console.warn('Empty state container not found');
          
          // Fallback to old structure if empty state container not found
          const emptyMessage = cartContent.querySelector('[data-element="empty-cart-message"]');
          const shopNowButton = cartContent.querySelector('[data-element="shop-now-button"]');
          
          if (emptyMessage) emptyMessage.style.display = 'block';
          if (shopNowButton) shopNowButton.style.display = 'block';
        }
        
        // Hide checkout button
        const checkoutButton = document.querySelector('[data-element="checkout-button"]');
        if (checkoutButton) {
          checkoutButton.disabled = true;
        }
        
        // Remove all cart items except the test product
        const existingItems = cartContent.querySelectorAll('[data-element="cart-item"]');
        existingItems.forEach(item => {
          if (item.getAttribute('data-product-id') !== 'Test-Product-1') {
            item.remove();
          }
        });
      } else {
        // Hide empty state container
        if (emptyState) {
          emptyState.style.display = 'none';
          console.log('Hiding empty state container');
        } else {
          console.warn('Empty state container not found');
          
          // Fallback to old structure if empty state container not found
          const emptyMessage = cartContent.querySelector('[data-element="empty-cart-message"]');
          const shopNowButton = cartContent.querySelector('[data-element="shop-now-button"]');
          
          if (emptyMessage) emptyMessage.style.display = 'none';
          if (shopNowButton) shopNowButton.style.display = 'none';
        }
        
        // Enable checkout button
        const checkoutButton = document.querySelector('[data-element="checkout-button"]');
        if (checkoutButton) {
          checkoutButton.disabled = false;
        }
        
        // Remove all cart items except the test product
        const existingItems = cartContent.querySelectorAll('[data-element="cart-item"]');
        existingItems.forEach(item => {
          if (item.getAttribute('data-product-id') !== 'Test-Product-1') {
            item.remove();
          }
        });
        
        // Generate cart items from template
        cart.items.forEach(item => {
          const cartItemElement = createCartItemFromTemplate(item);
          if (cartItemElement) {
            cartContent.appendChild(cartItemElement);
          }
        });
      }
      
      // Update total
      const totalElement = document.querySelector('[data-element="cart-total-text"]');
      if (totalElement) {
        totalElement.textContent = \`Total: $\${cart.total.toFixed(2)}\`;
        totalElement.setAttribute('data-amount', cart.total.toFixed(2));
      }
    } else {
      console.warn('Cart content element not found. Looking for [data-element="my-cart-content"]');
    }
  }
  
  // Handle Add to Cart button click
  function handleAddToCart(e) {
    const button = e.currentTarget;
    console.log('Add to cart button clicked:', button);
    
    // Get product data from button attributes
    const productId = button.getAttribute('data-product-id') || 'product-' + Date.now();
    let productName = button.getAttribute('data-product-name');
    const productPrice = button.getAttribute('data-price') || '0.00';
    const productImage = button.getAttribute('data-image') || '';
    
    // If product name is missing, try to find it
    if (!productName) {
      // Try to find a product name from nearby elements
      const parentElement = button.closest('[data-cms-item]');
      if (parentElement) {
        const heading = parentElement.querySelector('h1, h2, h3, h4, h5, h6');
        if (heading) {
          productName = heading.textContent.trim();
          // Add the attribute to the button for future clicks
          button.setAttribute('data-product-name', productName);
        }
      }
      
      // If still no product name, use a default
      if (!productName) {
        productName = 'Product';
        button.setAttribute('data-product-name', productName);
      }
    }
    
    // Create product object with properly formatted price
    const product = {
      id: productId,
      name: productName,
      price: parseFloat(productPrice).toFixed(2), // Ensure price is a number with 2 decimal places
      image: productImage
    };
    
    console.log('Product data:', product);
    
    // Add to cart
    addToCart(product);
    
    // Show feedback
    button.textContent = 'Added!';
    const originalBg = button.style.backgroundColor;
    button.style.backgroundColor = '#4CAF50';
    
    // Reset button after 2 seconds
    setTimeout(() => {
      button.textContent = 'Add to Cart';
      button.style.backgroundColor = originalBg;
    }, 2000);
  }
  
  // Update product data from CMS content
  function updateProductDataFromCMS() {
    // Find all CMS content items
    const cmsItems = document.querySelectorAll('[data-cms-item]');
    
    cmsItems.forEach(item => {
      // Find Add to Cart buttons within this CMS item
      const addToCartButtons = item.querySelectorAll('[data-action="add-to-cart"]');
      
      addToCartButtons.forEach(button => {
        // If button doesn't have product name, try to find it
        if (!button.getAttribute('data-product-name')) {
          const heading = item.querySelector('h1, h2, h3, h4, h5, h6');
          if (heading) {
            button.setAttribute('data-product-name', heading.textContent.trim());
          }
        }
        
        // If button doesn't have product image, try to find it
        if (!button.getAttribute('data-image')) {
          const image = item.querySelector('img');
          if (image) {
            button.setAttribute('data-image', image.src);
          }
        }
      });
    });
  }
  
  // Initialize cart functionality when DOM is loaded
  document.addEventListener('DOMContentLoaded', function() {
    console.log('DOM loaded, initializing cart functionality');
    
    // Initialize cart item template
    initializeCartItemTemplate();
    
    // Debug cart elements
    const cartElements = {
      container: document.querySelector('[data-element="cart-container"]'),
      wrapper: document.querySelector('[data-element="cart-wrapper"]'),
      content: document.querySelector('[data-element="my-cart-content"]'),
      button: document.querySelector('[data-element="cart-button"]'),
      emptyState: document.querySelector('[data-element="empty-state"]'),
      total: document.querySelector('[data-element="cart-total"]')
    };
    
    console.log('Cart elements found:', Object.entries(cartElements).reduce((acc, [key, value]) => {
      acc[key] = value ? true : false;
      return acc;
    }, {}));
    
    // Ensure cart container and wrapper have initial state set
    if (cartElements.container && cartElements.wrapper) {
      // Make sure the cart is initially hidden
      cartElements.container.style.display = 'none';
      cartElements.wrapper.style.display = 'none';
      cartElements.container.setAttribute('aria-hidden', 'true');
      cartElements.wrapper.setAttribute('aria-hidden', 'true');
    }
    
    // Add event listeners to Add to Cart buttons
    document.querySelectorAll('[data-action="add-to-cart"]').forEach(button => {
      button.onclick = handleAddToCart;
      console.log('Added click listener to Add to Cart button:', button);
    });
    
    // Add event listener to cart button
    const cartButton = document.querySelector('[data-element="cart-button"]');
    if (cartButton) {
      cartButton.onclick = function(e) {
        e.preventDefault();
        e.stopPropagation();
        console.log('Cart button clicked');
        toggleCart();
      };
      console.log('Added click listener to cart button');
    } else {
      console.error('Cart button not found');
    }
    
    // Add event listener to close cart button
    const closeCartButton = document.querySelector('.close-cart-button');
    if (closeCartButton) {
      closeCartButton.onclick = function(e) {
        e.preventDefault();
        e.stopPropagation();
        console.log('Close cart button clicked');
        hideCart();
      };
      console.log('Added click listener to close cart button');
    }
    
    // Add event listener to shop now button
    const shopNowButton = document.querySelector('[data-element="shop-now-button"]');
    if (shopNowButton) {
      shopNowButton.onclick = function(e) {
        e.preventDefault();
        e.stopPropagation();
        console.log('Shop now button clicked');
        hideCart();
      };
      console.log('Added click listener to shop now button');
    }
    
    // Add click outside to close cart
    document.addEventListener('click', function(e) {
      const cartWrapper = document.querySelector('[data-element="cart-wrapper"]');
      const cartButton = document.querySelector('[data-element="cart-button"]');
      
      if (cartWrapper && 
          window.getComputedStyle(cartWrapper).display !== 'none' && 
          !cartWrapper.contains(e.target) && 
          e.target !== cartButton) {
        console.log('Clicked outside cart');
        hideCart();
      }
    });
    
    // Load cart from localStorage
    loadCart();
    
    // Update product data from CMS content
    updateProductDataFromCMS();
    
    // Force an initial cart display update
    updateCartDisplay();
  });
`;

// Add checkout functionality
const checkoutJS = `
  document.addEventListener('DOMContentLoaded', function() {
    // Check if we're on the checkout page
    const checkoutSection = document.querySelector('[data-element="checkout-section"]');
    if (!checkoutSection) return;

    // Load cart items from storage
    const savedCart = localStorage.getItem('websiteBuilderPublishedCart');
    if (!savedCart) return;

    try {
      const cart = JSON.parse(savedCart);
      if (!cart.items || cart.items.length === 0) return;

      // Find the template order item
      const templateItem = document.querySelector('[data-element="checkout-order-item"][data-template="true"]');
      if (!templateItem) return;

      // Find the order items container
      const orderItemsContainer = document.querySelector('[data-element="checkout-order-items"]');
      if (!orderItemsContainer) return;

      // Remove existing order items (except template)
      const existingItems = orderItemsContainer.querySelectorAll('[data-element="checkout-order-item"]');
      existingItems.forEach(item => {
        if (item.getAttribute('data-template') !== 'true') {
          item.remove();
        }
      });

      // Hide the template item
      templateItem.style.display = 'none';

      // Calculate totals
      let subtotal = 0;
      const shippingCost = 9.99;
      const taxRate = 0.1; // 10% tax rate

      // Add each cart item
      cart.items.forEach(item => {
        const newItem = templateItem.cloneNode(true);
        newItem.removeAttribute('data-template');
        newItem.style.display = 'flex';

        // Update product name
        const nameElement = newItem.querySelector('[data-field="checkout-product-name"]');
        if (nameElement) nameElement.textContent = item.name;

        // Update quantity
        const quantityElement = newItem.querySelector('[data-field="checkout-product-quantity"]');
        if (quantityElement) quantityElement.textContent = \`x\${item.quantity}\`;

        // Calculate and update price
        const itemTotal = (parseFloat(item.price) * item.quantity).toFixed(2);
        const priceElement = newItem.querySelector('[data-field="checkout-product-price"]');
        if (priceElement) priceElement.textContent = \`$\${itemTotal}\`;

        subtotal += parseFloat(itemTotal);
        orderItemsContainer.appendChild(newItem);
      });

      // Calculate final totals
      const tax = subtotal * taxRate;
      const total = subtotal + shippingCost + tax;

      // Update shipping amount
      const shippingElement = document.querySelector('[data-field="shipping-price"]');
      if (shippingElement) shippingElement.textContent = \`$\${shippingCost.toFixed(2)}\`;

      // Update tax amount
      const taxElement = document.querySelector('[data-field="tax-amount"]');
      if (taxElement) taxElement.textContent = \`$\${tax.toFixed(2)}\`;

      // Update total amount
      const totalElement = document.querySelector('[data-field="total-amount"]');
      if (totalElement) totalElement.textContent = \`$\${total.toFixed(2)}\`;

    } catch (error) {
      console.error('Error populating checkout items:', error);
    }
  });
`;

const generateCustomCSS = (elements) => {
  let customCSS = '/* Custom CSS Styles */\n\n';

  const processCustomCSSContent = (css, builderId) => {
    if (!css) return '';

    // Replace & with the actual selector
    const selector = `[data-builder-id="${builderId}"]`;

    // Fix base styles that are not wrapped with a selector
    // First check if we have standalone styles at the start of the CSS
    const hasUnwrappedBaseStyles = /^[^@&\[{]/.test(css.trim());

    if (hasUnwrappedBaseStyles) {
      // Extract all standalone base styles at the beginning of the CSS
      let baseStyles = '';
      let remainingCSS = css;

      // Match all styles until we hit a selector or media query
      const match = css.match(/^([^@&\[{]+)(.*)$/s);
      if (match) {
        baseStyles = match[1].trim();
        remainingCSS = match[2].trim();

        // Create a properly wrapped base style rule
        const wrappedBaseStyles = `${selector} {\n  ${baseStyles}\n}\n\n`;

        // Replace the original CSS with the wrapped version
        css = wrappedBaseStyles + remainingCSS;
      }
    }

    // Replace & with the selector in all other rules
    css = css.replace(/&([:.][a-zA-Z-]+)?/g, (match, pseudo) => {
      return selector + (pseudo || '');
    });

    // Fix missing closing braces
    let openBraces = 0;
    let closeBraces = 0;

    for (let i = 0; i < css.length; i++) {
      if (css[i] === '{') openBraces++;
      if (css[i] === '}') closeBraces++;
    }

    // Add missing closing braces if needed
    while (closeBraces < openBraces) {
      css += '\n}';
      closeBraces++;
    }

    // Fix media queries to ensure they properly wrap their content
    css = css.replace(/@media[^{]+{([^}]*)}/g, (match, content) => {
      // If the media query content doesn't have the selector, wrap it
      if (!content.includes(selector)) {
        return match.replace(content, `\n  ${selector} {\n    ${content.trim()}\n  }\n`);
      }
      return match;
    });

    return css;
  };

  const processElement = (element) => {
    if (element['custom-css']) {
      const builderId = element.builderId;

      // Desktop styles (base)
      if (element['custom-css'][DEVICES.DESKTOP]) {
        customCSS += processCustomCSSContent(element['custom-css'][DEVICES.DESKTOP], builderId) + '\n\n';
      }

      // Tablet styles with media query
      if (element['custom-css'][DEVICES.TABLET]) {
        customCSS += `@media (max-width: 768px) {\n`;
        customCSS += processCustomCSSContent(element['custom-css'][DEVICES.TABLET], builderId) + '\n';
        customCSS += `}\n\n`;
      }

      // Mobile styles with media query
      if (element['custom-css'][DEVICES.MOBILE]) {
        customCSS += `@media (max-width: 375px) {\n`;
        customCSS += processCustomCSSContent(element['custom-css'][DEVICES.MOBILE], builderId) + '\n';
        customCSS += `}\n\n`;
      }
    }

    // Process child elements recursively
    if (element.children && element.children.length > 0) {
      element.children.forEach(child => processElement(child));
    }
  };

  // Process all root elements
  elements.forEach(element => processElement(element));

  return customCSS;
};

const generatePublishedBuild = async (pageData, allPages = [], siteFonts = []) => {
  // Define all JavaScript functions at the top
  const navigationJS = `
    document.addEventListener('DOMContentLoaded', function() {
      // Handle all interactions
      document.addEventListener('click', function(e) {
        const element = e.target.closest('[data-interaction]');
        if (!element) return;

        const interactionType = element.getAttribute('data-interaction');
        
        if (interactionType === 'navigation') {
          e.preventDefault();
          const pageId = element.getAttribute('data-page-id');
          
          if (pageId) {
            const pathParts = window.location.pathname.split('/');
            const domain = pathParts[3];
            
            // Check if this is a URL pattern based navigation
            if (pageId.startsWith('url-')) {
              const urlPattern = pageId.substring(4); // Remove 'url-' prefix
              if (urlPattern) {
                // Navigate to the custom URL pattern
                const newPath = '/' + urlPattern;
                window.location.href = newPath;
                return;
              }
            }
            
            // Default template ID based navigation
            const newPath = '/' + pageId;
            window.location.href = newPath;
          }
        } else if (interactionType === 'click') {
          e.preventDefault();
          
          // Check if this is a WhatsApp button
          if (element.getAttribute('data-element') === 'whatsapp-button') {
            const phoneNumber = element.getAttribute('data-whatsapp-phone');
            const message = element.getAttribute('data-whatsapp-message') || '';
            
            console.log('Original phone number:', phoneNumber);
            
            if (phoneNumber) {
              // Clean phone number - keep only digits and +
              let cleanPhone = '';
              for (let i = 0; i < phoneNumber.length; i++) {
                const char = phoneNumber[i];
                if (char === '+' || (char >= '0' && char <= '9')) {
                  cleanPhone += char;
                }
              }
              console.log('After cleaning:', cleanPhone);
              
              // Ensure phone number starts with + if it doesn't already
              if (!cleanPhone.startsWith('+')) {
                // If it starts with a country code without +, add it
                if (cleanPhone.length >= 8) {
                  cleanPhone = '+' + cleanPhone;
                } else {
                  console.error('Invalid phone number format:', phoneNumber);
                  return;
                }
              }
              
              console.log('Final clean phone:', cleanPhone);
              
              // Remove + for wa.me URL (wa.me expects numbers without +)
              const phoneForUrl = cleanPhone.substring(1);
              console.log('Phone for URL:', phoneForUrl, 'Length:', phoneForUrl.length);
              
              // Validate phone number length (should be at least 8 digits total including country code)
              if (phoneForUrl.length < 8) {
                console.error('Phone number too short:', phoneNumber);
                alert('Invalid phone number format. Please check the phone number.');
                return;
              }
              
              // Encode the message for URL
              const encodedMessage = encodeURIComponent(message);
              
              console.log('Encoded message:', encodedMessage);
              console.log('Expected working URL: https://wa.me/923028829260?text=' + encodedMessage);
              
              // Try multiple WhatsApp URL formats for better compatibility
              let whatsappUrl;
              
              // Check if we're on mobile device
              const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
              
              if (isMobile) {
                // For mobile devices, try the app-specific URL first
                whatsappUrl = \`whatsapp://send?phone=\${phoneForUrl}&text=\${encodedMessage}\`;
                
                // Try to open the app URL
                const appWindow = window.open(whatsappUrl, '_blank');
                
                // Fallback to web version after a short delay if app doesn't open
                setTimeout(() => {
                  const webUrl = \`https://wa.me/\${phoneForUrl}?text=\${encodedMessage}\`;
                  window.open(webUrl, '_blank');
                }, 1000);
                
                console.log('Mobile detected - trying app URL first:', whatsappUrl);
              } else {
                // For desktop, use web version directly
                whatsappUrl = \`https://wa.me/\${phoneForUrl}?text=\${encodedMessage}\`;
                window.open(whatsappUrl, '_blank');
                console.log('Desktop detected - using web URL:', whatsappUrl);
              }
              
              console.log('Opening WhatsApp with phone:', phoneForUrl, 'and message:', message);
              return;
            }
          }
          
          // Get action directly without using click counts
          if (element.hasAttribute('data-first-click')) {
            const action = element.getAttribute('data-first-click-action');
            
            // For toggleClass action, execute immediately on each click
            if (action === 'toggleClass') {
              handleClick(element, 'first');
              return;
            }
            
            // For other actions, use the click count system
            const elementId = element.id;
            const currentCount = clickCounts.get(elementId) || 0;
            
            // Check for first click
            if (currentCount === 0) {
              handleClick(element, 'first');
              clickCounts.set(elementId, 1);
            } 
            // Check for second click
            else if (currentCount === 1 && element.hasAttribute('data-second-click')) {
              handleClick(element, 'second');
              clickCounts.set(elementId, 0);
            } 
            // Reset if no valid click sequence
            else {
              clickCounts.set(elementId, 0);
            }
          }
        }
      });

      // Track click counts for elements
      const clickCounts = new Map();

      function handleClick(element, sequence) {
        const action = element.getAttribute('data-' + sequence + '-click-action');
        const targetId = element.getAttribute('data-' + sequence + '-click-target');
        const displayProperty = element.getAttribute('data-' + sequence + '-click-display');

        console.log('Handling click:', { sequence, action, targetId, displayProperty });

        if (action === 'toggle' && targetId) {
          const targetElement = document.getElementById(targetId);
          if (targetElement) {
            targetElement.style.display = displayProperty || 'block';
          }
        }
        else if (action === 'toggleClass' && targetId) {
          const targetElement = document.getElementById(targetId) || 
                              document.querySelector('[data-element-id="' + targetId + '"]') ||
                              document.querySelector('[data-builder-id="' + targetId + '"]');
          const className = element.getAttribute('data-' + sequence + '-click-class');
          
          if (targetElement && className) {
            // Toggle the class (add if not present, remove if present)
            targetElement.classList.toggle(className);
            console.log('Toggled class:', className, 'on element:', targetElement);
          }
        }
      }
    });
  `;

  // Form handling JavaScript
  const formJS = `
    function handleFormSubmit(event) {
      event.preventDefault();
      
      const form = event.target;
      const formData = new FormData(form);
      const data = Object.fromEntries(formData);
      
      console.log('Form data:', data);
      
      // Get the submit button to provide visual feedback
      const submitButton = form.querySelector('[type="submit"]') || form.querySelector('button[type="submit"]');
      const originalBgColor = submitButton ? window.getComputedStyle(submitButton).backgroundColor : '';
      const originalText = submitButton ? submitButton.innerText : '';
      
      // Visual feedback - change button appearance while processing
      if (submitButton) {
        submitButton.style.backgroundColor = '#999999'; // Grey color to indicate processing
        submitButton.innerText = 'Sending...';
        submitButton.disabled = true;
        submitButton.style.cursor = 'wait';
      }
      
      // Function to reset button state with optional success indication
      function resetButton(success = false) {
        if (!submitButton) return;
        
        // Re-enable the button
        submitButton.disabled = false;
        submitButton.style.cursor = 'pointer';
        
        if (success) {
          // Show success state briefly
          submitButton.style.backgroundColor = '#4CAF50'; // Green color for success
          submitButton.innerText = 'Sent!';
          
          // Reset to original state after a delay
          setTimeout(() => {
            submitButton.style.backgroundColor = originalBgColor;
            submitButton.innerText = originalText;
          }, 2000);
        } else {
          // Immediately reset to original state on error
          submitButton.style.backgroundColor = originalBgColor;
          submitButton.innerText = originalText;
        }
      }
      
      // Check if this is a checkout form
      if (form.getAttribute('name') === 'checkout-form') {
        console.log('Processing checkout form submission');
        
        // Load cart items from storage
        const savedCart = localStorage.getItem('websiteBuilderPublishedCart');
        if (!savedCart) {
          console.log('No cart data found');
          resetButton();
          return;
        }

        try {
          const cart = JSON.parse(savedCart);
          if (!cart.items || cart.items.length === 0) {
            console.log('Cart is empty');
            resetButton();
            return;
          }

          // Calculate order totals
          const subtotal = cart.items.reduce((total, item) => {
            return total + (parseFloat(item.price) * item.quantity);
          }, 0);

          const shippingCost = 9.99;
          const taxRate = 0.1; // 10% tax rate
          const tax = subtotal * taxRate;
          const total = subtotal + shippingCost + tax;

          // Create complete checkout data object
          const checkoutData = {
            customer: data,
            order: {
              items: cart.items,
              subtotal: subtotal.toFixed(2),
              shipping: shippingCost.toFixed(2),
              tax: tax.toFixed(2),
              total: total.toFixed(2)
            },
            isPublished: true
          };

          console.log('Complete checkout data:', checkoutData);

          // Get the site ID from the form
          const siteId = form.getAttribute('data-site-id');
          
          // Send order data to backend
          fetch(buildApiUrl(\`api/sites/\${siteId}/orders\`), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(checkoutData)
          })
          .then(response => response.json())
          .then(result => {
            if (result.success) {
              // Clear the cart
              localStorage.removeItem('websiteBuilderPublishedCart');
              
              // Show success message
              const successDiv = document.getElementById(form.getAttribute('data-success-div-id'));
              if (successDiv) {
                successDiv.style.display = 'block';
                form.reset();
              }
              resetButton(true);
            } else {
              throw new Error(result.error || 'Failed to create order');
            }
          })
          .catch(error => {
            console.error('Error creating order:', error);
            const errorDiv = document.getElementById(form.getAttribute('data-error-div-id'));
            if (errorDiv) {
              errorDiv.style.display = 'block';
            }
            resetButton(false);
          });
          
          return;
        } catch (error) {
          console.error('Error processing checkout data:', error);
          const errorDiv = document.getElementById(form.getAttribute('data-error-div-id'));
          if (errorDiv) errorDiv.style.display = 'block';
          resetButton(false);
          return;
        }
      }
      
      // Handle non-checkout forms like contact forms
      // First try to get success and error divs from data attributes
      let successDiv, errorDiv;
      
      // Look for success message using various methods including the specific Contact1 attributes
      successDiv = document.querySelector('[data-element="form-success-message"]') ||
                  document.querySelector('.contact1-form-success') ||
                  form.closest('[data-element="contact-form-wrapper"]')?.querySelector('.contact1-form-success') ||
                  document.getElementById(form.getAttribute('data-success-div-id'));
      
      // Look for error message using various methods including the specific Contact1 attributes
      errorDiv = document.querySelector('[data-element="form-error-message"]') ||
                document.querySelector('.contact1-form-error') ||
                form.closest('[data-element="contact-form-wrapper"]')?.querySelector('.contact1-form-error') ||
                document.getElementById(form.getAttribute('data-error-div-id'));
      
      // If not found, try looking for siblings or nearby containers (especially for Contact1 component)
      if (!successDiv || !errorDiv) {
        // Check if we're in a Contact1 form by looking at class names or data attributes
        const formWrapper = form.closest('[data-element="contact-form-wrapper"]') || 
                            form.closest('.contact1-form-wrapper');
        
        if (formWrapper) {
          // For Contact1 component, success and error messages are direct children of the wrapper
          successDiv = formWrapper.querySelector('.contact1-form-success') || 
                      formWrapper.querySelector('[data-element="form-success-message"]');
                      
          errorDiv = formWrapper.querySelector('.contact1-form-error') || 
                    formWrapper.querySelector('[data-element="form-error-message"]');
        } else {
          // Try finding messages as siblings of the form
          const formParent = form.parentElement;
          if (formParent) {
            successDiv = formParent.querySelector('.success-message') || 
                        formParent.querySelector('[data-success="true"]');
                        
            errorDiv = formParent.querySelector('.error-message') || 
                      formParent.querySelector('[data-error="true"]');
          }
        }
      }
      
      console.log('Found success div:', successDiv);
      console.log('Found error div:', errorDiv);
      
      // Hide both messages initially
      if (successDiv) successDiv.style.display = 'none';
      if (errorDiv) errorDiv.style.display = 'none';

      // Check for empty fields
      const hasEmptyFields = Object.values(data).some(value => !value.trim());
      
      if (hasEmptyFields) {
        if (errorDiv) {
          errorDiv.style.display = 'block';
        }
        resetButton(false);
        return;
      }

      // Add siteId to the form data
      const formDataWithSiteId = {
        ...data,
        siteId: form.getAttribute('data-site-id') || '1'
      };
      
      // Send form data to backend
      fetch(buildApiUrl('api/forms/submit'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formDataWithSiteId)
      })
      .then(response => response.json())
      .then(result => {
        if (result.success) {
          if (successDiv) {
            successDiv.style.display = 'block';
            form.reset();
          }
          resetButton(true);
        } else {
          if (errorDiv) {
            errorDiv.style.display = 'block';
          }
          resetButton(false);
        }
      })
      .catch(error => {
        console.log('Form submission error:', error);
        if (errorDiv) {
          errorDiv.style.display = 'block';
        }
        resetButton(false);
      })
      .finally(() => {
        // Ensure button is always reset even if there's an unexpected error
        setTimeout(() => {
          if (submitButton && submitButton.disabled) {
            resetButton(false);
          }
        }, 5000); // Failsafe: if button hasn't been reset after 5 seconds, force it
      });
    }
    
    // Initialize all forms when DOM is loaded
    document.addEventListener('DOMContentLoaded', function() {
      console.log('Initializing forms');
      // Find forms that don't have onsubmit attribute and add event listener
      const forms = document.querySelectorAll('form:not([onsubmit])');
      forms.forEach(form => {
        form.addEventListener('submit', handleFormSubmit);
        console.log('Added submit listener to form:', form);
      });
    });
  `;

  // Slider JavaScript
  const sliderJS = `
    // Debug function
    function debugSlider(message, ...args) {
      // Disabled for production
      // console.log('[Slider Debug]:', message, ...args);
    }

    // Function to get slider settings based on device width
    function getResponsiveSliderSettings(slider) {
      // Default values
      let settings = {
        slidesPerView: parseInt(slider.getAttribute('data-slides-per-view') || '1'),
        slidesPerScroll: parseInt(slider.getAttribute('data-slides-per-scroll') || '1'),
        loop: slider.getAttribute('data-loop') === 'true',
        autoplay: slider.getAttribute('data-autoplay') === 'true',
        delay: parseInt(slider.getAttribute('data-delay') || '5000')
      };
      
      // Get device-specific settings from data attributes
      let deviceSettings = {};
      try {
        const windowWidth = window.innerWidth;
        
        if (windowWidth <= 768) {
          // Mobile
          const mobileSettings = slider.getAttribute('data-mobile-settings');
          if (mobileSettings) {
            deviceSettings = JSON.parse(mobileSettings);
          }
        } else if (windowWidth <= 1024) {
          // Tablet
          const tabletSettings = slider.getAttribute('data-tablet-settings');
          if (tabletSettings) {
            deviceSettings = JSON.parse(tabletSettings);
          }
          } else {
          // Desktop
          const desktopSettings = slider.getAttribute('data-desktop-settings');
          if (desktopSettings) {
            deviceSettings = JSON.parse(desktopSettings);
          }
        }
        
        // Update settings with device-specific values
        if (deviceSettings.slidesPerView) {
          settings.slidesPerView = deviceSettings.slidesPerView;
        }
        if (deviceSettings.slidesPerScroll) {
          settings.slidesPerScroll = deviceSettings.slidesPerScroll;
        }
      } catch (e) {
        console.error('Error parsing slider settings:', e);
      }
      
      return settings;
    }

    function handleSliderArrowClick(event) {
      debugSlider('Arrow clicked', event.currentTarget);
      event.preventDefault();
      
      // Get the arrow element (either the button/link itself or its parent if clicked on a child image)
      const arrow = event.target.closest('[data-slider-role="arrow"]') || event.currentTarget;
      
      // Get direction from the arrow
      let direction = arrow.getAttribute('data-direction');
      
      // If direction attribute is missing, try to determine it
      if (!direction) {
        // Check aria-label for direction hints
        const ariaLabel = arrow.getAttribute('aria-label') || '';
        if (ariaLabel.includes('previous') || ariaLabel.includes('prev')) {
          direction = 'left';
        } else if (ariaLabel.includes('next')) {
          direction = 'right';
        }
        
        // If still no direction, try to infer from position
        if (!direction) {
          const computedStyle = window.getComputedStyle(arrow);
          const leftVal = computedStyle.left;
          const rightVal = computedStyle.right;
          
          if (leftVal !== 'auto' && rightVal === 'auto') {
            direction = 'left';
          } else if (rightVal !== 'auto' && leftVal === 'auto') {
            direction = 'right';
          }
        }
        
        // If still no direction, check image src for direction hints
        if (!direction) {
          const img = arrow.querySelector('img');
          if (img && img.src) {
            if (img.src.includes('left') || img.src.includes('prev') || img.src.includes('back')) {
              direction = 'left';
            } else if (img.src.includes('right') || img.src.includes('next') || img.src.includes('forward')) {
              direction = 'right';
            }
          }
        }
        
        // Last resort - check element id or class for direction hints
        if (!direction) {
          const id = arrow.id || '';
          const className = arrow.className || '';
          
          if (id.includes('left') || id.includes('prev') || className.includes('left') || className.includes('prev')) {
            direction = 'left';
          } else if (id.includes('right') || id.includes('next') || className.includes('right') || className.includes('next')) {
            direction = 'right';
          } else {
            // Default to right if we still can't determine direction
            direction = 'right';
          }
        }
        
        // Save the inferred direction as an attribute for future clicks
        arrow.setAttribute('data-direction', direction);
      }
      
      const container = arrow.closest('[data-slider-role="container"]');
      debugSlider('Container found:', container);
      
      // Find the mask element (first child or by query selector)
      if (!container) {
        console.error('No container found for the arrow!');
        return;
      }
      
      const mask = container.querySelector('[data-slider-role="mask"]');
      
      // Fallback to first child if query didn't work
      const firstChild = container.children[0];
      
      const finalMask = mask || firstChild;
      debugSlider('Mask found:', finalMask);
      debugSlider('Mask role:', finalMask?.getAttribute('data-slider-role'));
      
      if (!finalMask || !finalMask.hasAttribute('data-slider-role') || finalMask.getAttribute('data-slider-role') !== 'mask') {
        console.error('Invalid mask element:', finalMask);
        debugSlider('Invalid mask element');
        return;
      }
      
      // Get responsive settings
      const settings = getResponsiveSliderSettings(container);
      const slidesPerView = settings.slidesPerView;
      const slidesPerScroll = settings.slidesPerScroll;
      const loopEnabled = settings.loop;
      
      const slides = finalMask.children.length;
      let currentSlide = parseInt(finalMask.getAttribute('data-current-slide') || 0);
      
      if (direction === 'left') {
        if (currentSlide === 0 && loopEnabled) {
          // If at the beginning and loop is enabled, go to the end
          currentSlide = slides - slidesPerView;
        } else {
          // Normal navigation
          currentSlide = Math.max(0, currentSlide - slidesPerScroll);
        }
      } else {
        if (currentSlide >= slides - slidesPerView && loopEnabled) {
          // If at the end and loop is enabled, go to the beginning
          currentSlide = 0;
        } else {
          // Normal navigation
          currentSlide = Math.min(slides - slidesPerView, currentSlide + slidesPerScroll);
        }
      }
      
      const transformValue = \`translateX(-\${currentSlide * (100/slidesPerView)}%)\`;
      finalMask.style.transform = transformValue;
      finalMask.setAttribute('data-current-slide', currentSlide.toString());
    }

    // Variables to track dragging state
    let isDragging = false;
    let startPosX = 0;
    let currentTranslateX = 0;
    let previousTranslateX = 0;
    let currentMask = null;
    let currentSlider = null;
    
    // Store autoplay timers by slider ID
    const autoplayTimers = {};

    // Handle drag start
    function handleDragStart(event) {
      // Only handle left mouse button or touch events
      if (event.type === 'mousedown' && event.button !== 0) return;
      
      // Determine if we're clicking on a mask element
      const mask = event.target.closest('[data-slider-role="mask"]');
      if (!mask) return;
      
      // Find the slider container
      const slider = mask.closest('[data-slider-role="container"]');
      if (!slider) return;
      
      // Pause autoplay while dragging
      pauseAutoplay(slider);
      
      // Set current elements
      currentMask = mask;
      currentSlider = slider;

      // Get current position
      const clientX = event.type === 'touchstart' ? event.touches[0].clientX : event.clientX;
      startPosX = clientX;
      
      // Get current transform
      const currentTransform = window.getComputedStyle(mask).getPropertyValue('transform');
      const matrix = new DOMMatrixReadOnly(currentTransform);
      previousTranslateX = matrix.m41; // Get the X translation value
      currentTranslateX = previousTranslateX;
      
      // Set dragging state
      isDragging = true;
      
      // Add event listeners for drag and end
      if (event.type === 'touchstart') {
        document.addEventListener('touchmove', handleDrag, { passive: false });
        document.addEventListener('touchend', handleDragEnd);
        } else {
        document.addEventListener('mousemove', handleDrag);
        document.addEventListener('mouseup', handleDragEnd);
      }
      
      // Prevent default behavior (text selection, etc.)
      event.preventDefault();
    }

    // Handle dragging
    function handleDrag(event) {
      if (!isDragging || !currentMask || !currentSlider) return;
      
      // Get current pointer position
      const clientX = event.type === 'touchmove' ? event.touches[0].clientX : event.clientX;
      const deltaX = clientX - startPosX;
      
      // Calculate new position
      currentTranslateX = previousTranslateX + deltaX;
      
      // Apply transform directly
      currentMask.style.transition = 'none'; // Disable transition for smooth dragging
      currentMask.style.transform = \`translateX(\${currentTranslateX}px)\`;
      
      // Prevent page scrolling when dragging
      if (event.type === 'touchmove') {
        event.preventDefault();
      }
    }

    // Handle drag end with snap to slide
    function handleDragEnd(event) {
      if (!isDragging || !currentMask || !currentSlider) return;
      
      // Re-enable transition for smooth snap
      currentMask.style.transition = 'transform 0.3s ease-out';
      
      // Get responsive settings
      const settings = getResponsiveSliderSettings(currentSlider);
      const slidesPerView = settings.slidesPerView;
      const loopEnabled = settings.loop;
      
      // Calculate width of one slide in pixels
      const maskWidth = currentMask.offsetWidth;
      const slideWidthPercent = 100 / slidesPerView;
      const slideWidthPixels = (maskWidth * slideWidthPercent) / 100;
      
      // Calculate the current slide based on drag position
      const totalSlides = currentMask.children.length;
      const maxOffset = -((totalSlides - slidesPerView) * slideWidthPixels);
      
      // Threshold: if dragged more than 20% of slide width, snap to next slide
      const dragThreshold = slideWidthPixels * 0.2;
      
      // Calculate which slide to snap to
      let snapPosition;
      let newCurrentSlide;
      
      // If dragged significantly
      if (Math.abs(currentTranslateX - previousTranslateX) > dragThreshold) {
        // Dragged right (previous slides)
        if (currentTranslateX > previousTranslateX) {
          if (Math.floor(-previousTranslateX / slideWidthPixels) === 0 && loopEnabled) {
            newCurrentSlide = totalSlides - slidesPerView;
          } else {
            newCurrentSlide = Math.max(0, Math.floor(-previousTranslateX / slideWidthPixels) - 1);
          }
        } 
        // Dragged left (next slides)
        else {
          if (Math.ceil(-previousTranslateX / slideWidthPixels) >= totalSlides - slidesPerView && loopEnabled) {
            newCurrentSlide = 0;
          } else {
            newCurrentSlide = Math.min(totalSlides - slidesPerView, Math.ceil(-previousTranslateX / slideWidthPixels) + 1);
          }
        }
      } else {
        // Not dragged enough, snap back to current slide
        newCurrentSlide = Math.round(-previousTranslateX / slideWidthPixels);
      }
      
      // Ensure we're within bounds
      if (!loopEnabled) {
        newCurrentSlide = Math.max(0, Math.min(totalSlides - slidesPerView, newCurrentSlide));
      } else if (newCurrentSlide > totalSlides - slidesPerView) {
        newCurrentSlide = 0;
      } else if (newCurrentSlide < 0) {
        newCurrentSlide = totalSlides - slidesPerView;
      }
      
      // Calculate snap position in percentage for consistent behavior
      snapPosition = -(newCurrentSlide * (100 / slidesPerView));
      
      // Apply the transform with transition
      currentMask.style.transform = \`translateX(\${snapPosition}%)\`;
      currentMask.setAttribute('data-current-slide', newCurrentSlide.toString());
      
      // Clean up
      isDragging = false;
      
      // Resume autoplay if needed
      resumeAutoplay(currentSlider);
      
      // Clean up current elements reference after resuming autoplay
      currentMask = null;
      currentSlider = null;
      
      // Remove event listeners
      document.removeEventListener('mousemove', handleDrag);
      document.removeEventListener('mouseup', handleDragEnd);
      document.removeEventListener('touchmove', handleDrag);
      document.removeEventListener('touchend', handleDragEnd);
    }
    
    // Function to move to the next slide (for autoplay)
    function moveToNextSlide(slider) {
      if (!slider) return;
      
      const mask = slider.querySelector('[data-slider-role="mask"]');
      if (!mask) return;
      
      const settings = getResponsiveSliderSettings(slider);
      const slidesPerView = settings.slidesPerView;
      const slidesPerScroll = settings.slidesPerScroll;
      const loopEnabled = settings.loop;
      
      const totalSlides = mask.children.length;
      let currentSlide = parseInt(mask.getAttribute('data-current-slide') || '0');
      
      if (currentSlide >= totalSlides - slidesPerView) {
        if (loopEnabled) {
          // If loop is enabled, go back to the first slide
          currentSlide = 0;
        } else {
          // If loop is disabled, stop autoplay
          pauseAutoplay(slider);
          return;
        }
      } else {
        // Move to the next slide
        currentSlide += slidesPerScroll;
        // Make sure we don't exceed the maximum
        if (!loopEnabled) {
          currentSlide = Math.min(totalSlides - slidesPerView, currentSlide);
        }
      }
      
      mask.style.transition = 'transform 0.5s ease-out';
      mask.style.transform = \`translateX(-\${currentSlide * (100/slidesPerView)}%)\`;
      mask.setAttribute('data-current-slide', currentSlide.toString());
    }
    
    // Function to start autoplay for a slider
    function startAutoplay(slider) {
      if (!slider) return;
      
      const settings = getResponsiveSliderSettings(slider);
      if (!settings.autoplay) return;
      
      // Clear any existing timer
      pauseAutoplay(slider);
      
      // Create a new timer
      const sliderId = slider.id || 'slider-' + Math.random().toString(36).substr(2, 9);
      slider.id = sliderId;
      
      autoplayTimers[sliderId] = setInterval(() => {
        moveToNextSlide(slider);
      }, settings.delay);
    }
    
    // Function to pause autoplay for a slider
    function pauseAutoplay(slider) {
      if (!slider || !slider.id) return;
      
      clearInterval(autoplayTimers[slider.id]);
      delete autoplayTimers[slider.id];
    }
    
    // Function to resume autoplay if it was enabled
    function resumeAutoplay(slider) {
      if (!slider) return;
      
      const settings = getResponsiveSliderSettings(slider);
      if (settings.autoplay) {
        startAutoplay(slider);
      }
    }

    // Initialize sliders and apply responsive settings
    function initializeSliders() {
      const sliders = document.querySelectorAll('[data-slider-role="container"]');
      sliders.forEach(slider => {
        const mask = slider.querySelector('[data-slider-role="mask"]');
        if (!mask) {
          return;
        }
        
        // Set initial slide and update slide widths
        mask.setAttribute('data-current-slide', '0');
        updateSliderSlideWidths(slider);
        
        // Add mouse and touch event listeners for drag functionality
        mask.addEventListener('mousedown', handleDragStart);
        mask.addEventListener('touchstart', handleDragStart, { passive: false });
        
        // Initialize autoplay if enabled
        const settings = getResponsiveSliderSettings(slider);
        if (settings.autoplay) {
          startAutoplay(slider);
        }
        
        let arrows = slider.querySelectorAll('[data-slider-role="arrow"]');

        // If no arrows found, try to find links or buttons inside the slider that could be arrows
        if (arrows.length === 0) {
          // Look for elements that are likely to be arrows
          const potentialArrows = slider.querySelectorAll('a, button');
          const confirmedArrows = [];
          
          potentialArrows.forEach(el => {
            // Check aria-label for hints that this is an arrow
            const ariaLabel = el.getAttribute('aria-label') || '';
            if (ariaLabel.includes('slide') || ariaLabel.includes('previous') || ariaLabel.includes('next')) {
              // Determine direction from aria-label
              let direction;
              if (ariaLabel.includes('previous') || ariaLabel.includes('prev')) {
                direction = 'left';
              } else if (ariaLabel.includes('next')) {
                direction = 'right';
              }
              
              // Add data attributes to make it a proper arrow
              el.setAttribute('data-slider-role', 'arrow');
              if (direction) {
                el.setAttribute('data-direction', direction);
              }
              
              confirmedArrows.push(el);
            }
            // If no aria-label, check for child images which might indicate an arrow
            else if (el.querySelector('img')) {
              const img = el.querySelector('img');
              const imgSrc = img.getAttribute('src') || '';
              
              // Try to determine direction from image src
              if (imgSrc.includes('left') || imgSrc.includes('prev') || imgSrc.includes('back')) {
                el.setAttribute('data-slider-role', 'arrow');
                el.setAttribute('data-direction', 'left');
                confirmedArrows.push(el);
              } else if (imgSrc.includes('right') || imgSrc.includes('next') || imgSrc.includes('forward')) {
                el.setAttribute('data-slider-role', 'arrow');
                el.setAttribute('data-direction', 'right');
                confirmedArrows.push(el);
              }
            }
          });
          
          // Use confirmed arrows if we found any
          if (confirmedArrows.length > 0) {
            arrows = confirmedArrows;
          }
        }
        
        arrows.forEach(arrow => {
          // Handle clicks on the arrow itself
          arrow.addEventListener('click', handleSliderArrowClick);
          
          // Also handle clicks on any child elements (like images)
          const children = Array.from(arrow.children);
          children.forEach(child => {
            child.addEventListener('click', (e) => {
              e.stopPropagation(); // Prevent double-firing
              handleSliderArrowClick.call(arrow, e); // Call with arrow as this context
            });
          });
        });
      });
    }
    
    // Update slide widths based on responsive settings
    function updateSliderSlideWidths(slider) {
      const mask = slider.querySelector('[data-slider-role="mask"]');
      if (!mask) return;
      
      const settings = getResponsiveSliderSettings(slider);
      const slides = mask.querySelectorAll('[data-slider-role="slide"]');
      
      slides.forEach(slide => {
        const flexValue = 100/settings.slidesPerView;
        slide.style.flex = \`0 0 \${flexValue}%\`;
      });
      
      // Reset transform when device size changes
      mask.style.transform = 'translateX(0)';
      mask.setAttribute('data-current-slide', '0');
      
      // Reset and restart autoplay
      if (settings.autoplay) {
        pauseAutoplay(slider);
        startAutoplay(slider);
      }
    }
    
    // Listen for window resize events to update slider settings
    window.addEventListener('resize', () => {
      // Debounce the resize event
      clearTimeout(window.resizeTimeout);
      window.resizeTimeout = setTimeout(() => {
        const sliders = document.querySelectorAll('[data-slider-role="container"]');
        sliders.forEach(updateSliderSlideWidths);
      }, 250);
    });
    
    // Initialize sliders when DOM is loaded
    document.addEventListener('DOMContentLoaded', function() {
      initializeSliders();
      
      // Also add direct click handlers as a fallback
      const allArrows = document.querySelectorAll('[data-slider-role="arrow"]');
      allArrows.forEach(arrow => {
        arrow.addEventListener('click', function(e) {
          handleSliderArrowClick(e);
        });
      });
    });
  `;

  let pageContent = [];

  // Helper function to process dynamic fields in a string
  const processDynamicFields = (text, contentItem) => {
    if (!text || !contentItem) return text;

    // Replace [field:name] with actual content values
    return text.replace(/\[field:([^\]]+)\]/g, (match, fieldName) => {
      return contentItem.data[fieldName] || match;
    });
  };

  // Generate font links for HTML head
  const generateFontLinks = (siteFonts) => {
    if (!siteFonts || !Array.isArray(siteFonts) || siteFonts.length === 0) {
      return '';
    }

    return siteFonts
      .filter(fontRef => fontRef.font && fontRef.font.googleUrl)
      .map(fontRef => `<link href="${fontRef.font.googleUrl}" rel="stylesheet">`)
      .join('\n            ');
  };

  // For template pages, generate separate HTML for each content item
  if (pageData.isTemplate && pageData.contentTypeId) {
    try {
      // Get siteId from URL
      const siteId = window.location.pathname.split('/editor/')[1];

      // Fetch all content items for this template
      const response = await fetch(buildApiUrl(`api/cms/content/${siteId}/${pageData.contentTypeId}`));
      const data = await response.json();
      if (data.success) {
        pageContent = data.content;
      }
    } catch (error) {
      console.error('Error fetching template content:', error);
    }

    // Generate separate HTML for each content item
    const pages = {};


    pageContent.forEach(contentItem => {
      // Create a special content map with URL pattern information
      const contentWithUrlPattern = {};

      // Add the current content item to the map with its content type ID as the key
      contentWithUrlPattern[pageData.contentTypeId] = [contentItem];

      // Add URL pattern information to the content map
      contentWithUrlPattern.__urlPatterns = {};

      // Add the current page's URL pattern
      if (pageData.template?.urlPattern) {
        contentWithUrlPattern.__urlPatterns[pageData.contentTypeId] = pageData.template.urlPattern;
      }

      // Add URL patterns from all template pages
      if (allPages && Array.isArray(allPages)) {
        allPages.forEach(page => {
          if (page.isTemplate && page.contentTypeId && page.template?.urlPattern) {
            contentWithUrlPattern.__urlPatterns[page.contentTypeId] = page.template.urlPattern;
          }
        });
      }

      console.log('Content with URL pattern for template page:', contentWithUrlPattern);

      const html = generateHTML(
        pageData.elements,
        contentWithUrlPattern,
        pageData.classStylesRegistry,
        pageData.combinedClassStylesRegistry,
        allPages,
        contentItem  // Pass the specific content item as the currentContentItem parameter
      );

      // Generate CSS for both individual and combined classes
      const generateCombinedCSS = (classStylesRegistry, combinedClassStylesRegistry) => {
        let css = '';

        // Helper to group classes by their base name and states
        const groupClasses = (registry) => {
          const classGroups = {};
          registry.forEach((styles, className) => {
            if (className.includes('--')) {
              const [baseClass] = className.split('--');
              if (!classGroups[baseClass]) {
                classGroups[baseClass] = { base: null, states: new Set() };
              }
              classGroups[baseClass].states.add(className);
            } else {
              if (!classGroups[className]) {
                classGroups[className] = { base: className, states: new Set() };
              } else {
                classGroups[className].base = className;
              }
            }
          });
          return classGroups;
        };

        // Handle single classes and their states
        Object.entries(classStylesRegistry).forEach(([device, registry]) => {
          if (registry.size === 0) return;

          const classGroups = groupClasses(registry);

          if (device === 'desktop') {
            // Generate base and state styles for desktop
            Object.entries(classGroups).forEach(([baseClass, { base, states }]) => {
              // Base class styles
              if (base) {
                const baseStyles = registry.get(base) || {};
                css += `.${base} {\n`;
                Object.entries(baseStyles).forEach(([prop, value]) => {
                  const cssKey = prop.replace(/([A-Z])/g, '-$1').toLowerCase();
                  css += `  ${cssKey}: ${value};\n`;
                });
                css += '}\n';
              }

              // State styles
              states.forEach(stateClass => {
                const [, state] = stateClass.split('--');
                const pseudoClass = state === 'pressed' ? 'active' :
                  state === 'focused' ? 'focus' :
                    state;
                const stateStyles = registry.get(stateClass) || {};

                css += `.${baseClass}:${pseudoClass} {\n`;
                Object.entries(stateStyles).forEach(([prop, value]) => {
                  const cssKey = prop.replace(/([A-Z])/g, '-$1').toLowerCase();
                  css += `  ${cssKey}: ${value};\n`;
                });
                css += `  transition: all 0.3s ease;\n`;
                css += '}\n';
              });
            });
          } else {
            // Generate media query wrapped styles for tablet and mobile
            css += `@media (max-width: ${device === 'mobile' ? '375px' : '768px'}) {\n`;

            Object.entries(classGroups).forEach(([baseClass, { base, states }]) => {
              // Base class styles
              if (base) {
                const baseStyles = registry.get(base) || {};
                css += `  .${base} {\n`;
                Object.entries(baseStyles).forEach(([prop, value]) => {
                  const cssKey = prop.replace(/([A-Z])/g, '-$1').toLowerCase();
                  css += `    ${cssKey}: ${value};\n`;
                });
                css += '  }\n';
              }

              // State styles
              states.forEach(stateClass => {
                const [, state] = stateClass.split('--');
                const pseudoClass = state === 'pressed' ? 'active' :
                  state === 'focused' ? 'focus' :
                    state;
                const stateStyles = registry.get(stateClass) || {};

                css += `  .${baseClass}:${pseudoClass} {\n`;
                Object.entries(stateStyles).forEach(([prop, value]) => {
                  const cssKey = prop.replace(/([A-Z])/g, '-$1').toLowerCase();
                  css += `    ${cssKey}: ${value};\n`;
                });
                css += `    transition: all 0.3s ease;\n`;
                css += '  }\n';
              });
            });

            css += '}\n';
          }
        });

        // Handle combined classes and their states
        Object.entries(combinedClassStylesRegistry).forEach(([device, registry]) => {
          if (registry.size === 0) return;

          const combinedClassGroups = groupClasses(registry);

          if (device === 'desktop') {
            // Generate base and state styles for desktop
            Object.entries(combinedClassGroups).forEach(([baseCombination, { base, states }]) => {
              // Base combined class styles
              if (base) {
                const baseStyles = registry.get(base) || {};
                const combinedSelectors = base.split('+').map(c => `.${c}`).join('');

                css += `${combinedSelectors} {\n`;
                Object.entries(baseStyles).forEach(([prop, value]) => {
                  const cssKey = prop.replace(/([A-Z])/g, '-$1').toLowerCase();
                  css += `  ${cssKey}: ${value};\n`;
                });
                css += '}\n';
              }

              // State styles for combined classes
              states.forEach(stateClass => {
                const [, state] = stateClass.split('--');
                const pseudoClass = state === 'pressed' ? 'active' :
                  state === 'focused' ? 'focus' :
                    state;
                const stateStyles = registry.get(stateClass) || {};
                const combinedSelectors = baseCombination.split('+')
                  .map(c => `.${c}:${pseudoClass}`)
                  .join('');

                css += `${combinedSelectors} {\n`;
                Object.entries(stateStyles).forEach(([prop, value]) => {
                  const cssKey = prop.replace(/([A-Z])/g, '-$1').toLowerCase();
                  css += `  ${cssKey}: ${value};\n`;
                });
                css += `  transition: all 0.3s ease;\n`;
                css += '}\n';
              });
            });
          } else {
            // Generate media query wrapped styles for tablet and mobile
            css += `@media (max-width: ${device === 'mobile' ? '375px' : '768px'}) {\n`;

            Object.entries(combinedClassGroups).forEach(([baseCombination, { base, states }]) => {
              // Base combined class styles
              if (base) {
                const baseStyles = registry.get(base) || {};
                const combinedSelectors = base.split('+').map(c => `.${c}`).join('');

                css += `  ${combinedSelectors} {\n`;
                Object.entries(baseStyles).forEach(([prop, value]) => {
                  const cssKey = prop.replace(/([A-Z])/g, '-$1').toLowerCase();
                  css += `    ${cssKey}: ${value};\n`;
                });
                css += '  }\n';
              }

              // State styles for combined classes
              states.forEach(stateClass => {
                const [, state] = stateClass.split('--');
                const pseudoClass = state === 'pressed' ? 'active' :
                  state === 'focused' ? 'focus' :
                    state;
                const stateStyles = registry.get(stateClass) || {};
                const combinedSelectors = baseCombination.split('+')
                  .map(c => `.${c}:${pseudoClass}`)
                  .join('');

                css += `  ${combinedSelectors} {\n`;
                Object.entries(stateStyles).forEach(([prop, value]) => {
                  const cssKey = prop.replace(/([A-Z])/g, '-$1').toLowerCase();
                  css += `    ${cssKey}: ${value};\n`;
                });
                css += `    transition: all 0.3s ease;\n`;
                css += '  }\n';
              });
            });

            css += '}\n';
          }
        });

        return css;
      };

      const css = generateCombinedCSS(
        pageData.classStylesRegistry,
        pageData.combinedClassStylesRegistry
      );

      // Process SEO settings with dynamic fields
      const pageTitle = pageData.seo?.title
        ? processDynamicFields(pageData.seo.title, contentItem)
        : (contentItem ? contentItem.data.title : pageData.name);

      const pageDescription = pageData.seo?.description
        ? processDynamicFields(pageData.seo.description, contentItem)
        : '';

      // Generate URL based on template pattern
      let pageUrl = '';
      if (pageData.template?.urlPattern) {
        pageUrl = processDynamicFields(pageData.template.urlPattern, contentItem);
      } else {
        pageUrl = `${pageData.contentTypeId}/${contentItem._id}`;
      }

      // Generate custom CSS
      const customCss = generateCustomCSS(pageData.elements);

      // Process custom code with dynamic fields if available
      const customHeadCode = pageData.customCode?.head
        ? processDynamicFields(pageData.customCode.head, contentItem)
        : '';

      const customBodyCode = pageData.customCode?.body
        ? processDynamicFields(pageData.customCode.body, contentItem)
        : '';

      // Generate font CSS and links
      const fontLinks = generateFontLinks(siteFonts);
      const fontCSS = generateFontCSS(siteFonts);

      const indexHTML = `
        <!DOCTYPE html>
        <html lang="en">
        <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>${pageTitle}</title>
            <meta name="description" content="${pageDescription}">
            <link rel="canonical" href="https://yourdomain.com/${pageUrl}">
            <link rel="stylesheet" href="custom.css">
            <link href="https://fonts.googleapis.com/css2?family=Arial&family=Roboto&family=Open+Sans&family=Lato&family=Poppins&family=Montserrat&family=Source+Sans+Pro&family=Inter&display=swap" rel="stylesheet">
            <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css">
            ${fontLinks}
            <style>
              /* Reset styles */
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              
              /* Font variables */
              ${fontCSS}
              
              /* Template hiding styles */
              .template-hidden {
                display: none !important;
                visibility: hidden !important;
                position: absolute !important;
                width: 0 !important;
                height: 0 !important;
                overflow: hidden !important;
                clip: rect(0 0 0 0) !important;
                -webkit-clip-path: inset(50%) !important;
                clip-path: inset(50%) !important;
                white-space: nowrap !important;
                border: 0 !important;
                padding: 0 !important;
                margin: 0 !important;
              }
              
              /* Your generated CSS */
              ${css}

              /* CMS Element Styles */
              .cms-content-container.grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
                gap: 16px;
              }
              .cms-content-container.cards {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
                gap: 24px;
              }
              .cms-content-container.list {
                display: flex;
                flex-direction: column;
                gap: 16px;
              }
              .cms-content-item {
                padding: 16px;
                background: #fff;
                border-radius: 4px;
                box-shadow: 0 2px 4px rgba(0,0,0,0.1);
              }
            </style>
            <!-- Custom head code -->
            ${customHeadCode}
        </head>
        <body>
            <div id="root">
                ${html}
            </div>
            <script>
                window.addEventListener('load', function() {
                    const sliders = document.querySelectorAll('[data-slider-role="container"]');
                    const arrows = document.querySelectorAll('[data-slider-role="arrow"]');
                    
                    // Add direct event listeners as a last resort
                    arrows.forEach(arrow => {
                        arrow.onclick = function(e) {
                            e.preventDefault();
                        };
                    });
                });
            </script>
            <script>${navigationJS}</script>
            <script>${formJS}</script>
            <script>${sliderJS}</script>
            <script>${cartJS}</script>
            <script>${checkoutJS}</script>
            <!-- Custom body code -->
            ${customBodyCode}
        </body>
        </html>
      `;

      // Generate page ID based on URL pattern if available
      const pageId = `template-${pageData.contentTypeId}-${contentItem._id}`;
      pages[pageId] = {
        name: pageTitle,
        isTemplate: true,
        contentTypeId: pageData.contentTypeId,
        contentItemId: contentItem._id,
        url: pageUrl,
        buildFiles: {
          isTemplate: false,
          indexHTML,
          css,
          customCss,
          js: navigationJS
        }
      };
    });

    return {
      isTemplate: true,
      contentTypeId: pageData.contentTypeId,
      pages
    };
  }

  // For regular pages with CMS elements
  const cmsElements = findCMSElements(pageData.elements);
  if (cmsElements.length > 0) {
    // Get siteId from URL
    const siteId = window.location.pathname.split('/editor/')[1];

    const contentPromises = cmsElements.map(async element => {
      try {
        console.log('Fetching content for CMS element:', element.contentTypeId);
        const response = await fetch(buildApiUrl(`api/cms/content/${siteId}/${element.contentTypeId}`));
        const data = await response.json();
        if (data.success) {
          console.log('Fetched content:', data.content);
          return {
            contentTypeId: element.contentTypeId,
            content: data.content
          };
        }
        return null;
      } catch (error) {
        console.error('Error fetching CMS content:', error);
        return null;
      }
    });

    const contentResponses = await Promise.all(contentPromises);
    const contentMap = {};

    // Create URL patterns map
    const urlPatterns = {};

    console.log('pageData:', pageData);

    // If the current page is a template with a URL pattern, add it to the patterns
    if (pageData.isTemplate && pageData.contentTypeId && pageData.template?.urlPattern) {
      urlPatterns[pageData.contentTypeId] = pageData.template.urlPattern;
      console.log(`Using URL pattern for content type ${pageData.contentTypeId}: ${pageData.template.urlPattern}`);
    }

    // Add URL patterns from all template pages
    if (allPages && Array.isArray(allPages)) {
      allPages.forEach(page => {
        if (page.isTemplate && page.contentTypeId && page.template?.urlPattern) {
          urlPatterns[page.contentTypeId] = page.template.urlPattern;
          console.log(`Found URL pattern for content type ${page.contentTypeId}: ${page.template.urlPattern}`);
        }
      });
    }



    contentResponses.forEach(response => {
      if (response) {
        contentMap[response.contentTypeId] = response.content;
      }
    });

    // Add URL patterns to the content map
    contentMap.__urlPatterns = urlPatterns;

    console.log('Final content map with URL patterns:', contentMap);
    pageContent = contentMap;
  }

  // For regular pages
  const html = generateHTML(
    pageData.elements,
    pageContent,
    pageData.classStylesRegistry,
    pageData.combinedClassStylesRegistry,
    allPages
  );

  // Generate CSS for both individual and combined classes
  const generateCombinedCSS = (classStylesRegistry, combinedClassStylesRegistry) => {
    let css = '';

    // Helper to group classes by their base name and states
    const groupClasses = (registry) => {
      const classGroups = {};
      registry.forEach((styles, className) => {
        if (className.includes('--')) {
          const [baseClass] = className.split('--');
          if (!classGroups[baseClass]) {
            classGroups[baseClass] = { base: null, states: new Set() };
          }
          classGroups[baseClass].states.add(className);
        } else {
          if (!classGroups[className]) {
            classGroups[className] = { base: className, states: new Set() };
          } else {
            classGroups[className].base = className;
          }
        }
      });
      return classGroups;
    };

    // Handle single classes and their states
    Object.entries(classStylesRegistry).forEach(([device, registry]) => {
      if (registry.size === 0) return;

      const classGroups = groupClasses(registry);

      if (device === 'desktop') {
        // Generate base and state styles for desktop
        Object.entries(classGroups).forEach(([baseClass, { base, states }]) => {
          // Base class styles
          if (base) {
            const baseStyles = registry.get(base) || {};
            css += `.${base} {\n`;
            Object.entries(baseStyles).forEach(([prop, value]) => {
              const cssKey = prop.replace(/([A-Z])/g, '-$1').toLowerCase();
              css += `  ${cssKey}: ${value};\n`;
            });
            css += '}\n';
          }

          // State styles
          states.forEach(stateClass => {
            const [, state] = stateClass.split('--');
            const pseudoClass = state === 'pressed' ? 'active' :
              state === 'focused' ? 'focus' :
                state;
            const stateStyles = registry.get(stateClass) || {};

            css += `.${baseClass}:${pseudoClass} {\n`;
            Object.entries(stateStyles).forEach(([prop, value]) => {
              const cssKey = prop.replace(/([A-Z])/g, '-$1').toLowerCase();
              css += `  ${cssKey}: ${value};\n`;
            });
            css += `  transition: all 0.3s ease;\n`;
            css += '}\n';
          });
        });
      } else {
        // Generate media query wrapped styles for tablet and mobile
        css += `@media (max-width: ${device === 'mobile' ? '375px' : '768px'}) {\n`;

        Object.entries(classGroups).forEach(([baseClass, { base, states }]) => {
          // Base class styles
          if (base) {
            const baseStyles = registry.get(base) || {};
            css += `  .${base} {\n`;
            Object.entries(baseStyles).forEach(([prop, value]) => {
              const cssKey = prop.replace(/([A-Z])/g, '-$1').toLowerCase();
              css += `    ${cssKey}: ${value};\n`;
            });
            css += '  }\n';
          }

          // State styles
          states.forEach(stateClass => {
            const [, state] = stateClass.split('--');
            const pseudoClass = state === 'pressed' ? 'active' :
              state === 'focused' ? 'focus' :
                state;
            const stateStyles = registry.get(stateClass) || {};

            css += `  .${baseClass}:${pseudoClass} {\n`;
            Object.entries(stateStyles).forEach(([prop, value]) => {
              const cssKey = prop.replace(/([A-Z])/g, '-$1').toLowerCase();
              css += `    ${cssKey}: ${value};\n`;
            });
            css += `    transition: all 0.3s ease;\n`;
            css += '  }\n';
          });
        });

        css += '}\n';
      }
    });

    // Handle combined classes and their states
    Object.entries(combinedClassStylesRegistry).forEach(([device, registry]) => {
      if (registry.size === 0) return;

      const combinedClassGroups = groupClasses(registry);

      if (device === 'desktop') {
        // Generate base and state styles for desktop
        Object.entries(combinedClassGroups).forEach(([baseCombination, { base, states }]) => {
          // Base combined class styles
          if (base) {
            const baseStyles = registry.get(base) || {};
            const combinedSelectors = base.split('+').map(c => `.${c}`).join('');

            css += `${combinedSelectors} {\n`;
            Object.entries(baseStyles).forEach(([prop, value]) => {
              const cssKey = prop.replace(/([A-Z])/g, '-$1').toLowerCase();
              css += `  ${cssKey}: ${value};\n`;
            });
            css += '}\n';
          }

          // State styles for combined classes
          states.forEach(stateClass => {
            const [, state] = stateClass.split('--');
            const pseudoClass = state === 'pressed' ? 'active' :
              state === 'focused' ? 'focus' :
                state;
            const stateStyles = registry.get(stateClass) || {};
            const combinedSelectors = baseCombination.split('+')
              .map(c => `.${c}:${pseudoClass}`)
              .join('');

            css += `${combinedSelectors} {\n`;
            Object.entries(stateStyles).forEach(([prop, value]) => {
              const cssKey = prop.replace(/([A-Z])/g, '-$1').toLowerCase();
              css += `  ${cssKey}: ${value};\n`;
            });
            css += `  transition: all 0.3s ease;\n`;
            css += '}\n';
          });
        });
      } else {
        // Generate media query wrapped styles for tablet and mobile
        css += `@media (max-width: ${device === 'mobile' ? '375px' : '768px'}) {\n`;

        Object.entries(combinedClassGroups).forEach(([baseCombination, { base, states }]) => {
          // Base combined class styles
          if (base) {
            const baseStyles = registry.get(base) || {};
            const combinedSelectors = base.split('+').map(c => `.${c}`).join('');

            css += `  ${combinedSelectors} {\n`;
            Object.entries(baseStyles).forEach(([prop, value]) => {
              const cssKey = prop.replace(/([A-Z])/g, '-$1').toLowerCase();
              css += `    ${cssKey}: ${value};\n`;
            });
            css += '  }\n';
          }

          // State styles for combined classes
          states.forEach(stateClass => {
            const [, state] = stateClass.split('--');
            const pseudoClass = state === 'pressed' ? 'active' :
              state === 'focused' ? 'focus' :
                state;
            const stateStyles = registry.get(stateClass) || {};
            const combinedSelectors = baseCombination.split('+')
              .map(c => `.${c}:${pseudoClass}`)
              .join('');

            css += `  ${combinedSelectors} {\n`;
            Object.entries(stateStyles).forEach(([prop, value]) => {
              const cssKey = prop.replace(/([A-Z])/g, '-$1').toLowerCase();
              css += `    ${cssKey}: ${value};\n`;
            });
            css += `    transition: all 0.3s ease;\n`;
            css += '  }\n';
          });
        });

        css += '}\n';
      }
    });

    return css;
  };

  const css = generateCombinedCSS(
    pageData.classStylesRegistry,
    pageData.combinedClassStylesRegistry
  );

  // Generate custom CSS for regular pages
  const customCss = generateCustomCSS(pageData.elements);

  // Get SEO settings for static page
  const pageTitle = pageData.seo?.title || pageData.name || 'Untitled Page';
  const pageDescription = pageData.seo?.description || '';
  const pageSlug = pageData.slug || '';

  // Get custom code for head and body
  const customHeadCode = pageData.customCode?.head || '';
  const customBodyCode = pageData.customCode?.body || '';

  // Generate font CSS and links
  const fontLinks = generateFontLinks(siteFonts);
  const fontCSS = generateFontCSS(siteFonts);

  // Update the indexHTML template to include SEO settings
  const indexHTML = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${pageTitle}</title>
        <meta name="description" content="${pageDescription}">
        <link rel="canonical" href="https://yourdomain.com/${pageSlug}">
        <link rel="stylesheet" href="custom.css">
        <link href="https://fonts.googleapis.com/css2?family=Arial&family=Roboto&family=Open+Sans&family=Lato&family=Poppins&family=Montserrat&family=Source+Sans+Pro&family=Inter&display=swap" rel="stylesheet">
        <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css">
        ${fontLinks}
        <style>
          /* Reset styles */
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
                      /* Font variables */
            ${fontCSS}
            
            /* Template hiding styles */
            .template-hidden {
              display: none !important;
              visibility: hidden !important;
              position: absolute !important;
              width: 0 !important;
              height: 0 !important;
              overflow: hidden !important;
              clip: rect(0 0 0 0) !important;
              -webkit-clip-path: inset(50%) !important;
              clip-path: inset(50%) !important;
              white-space: nowrap !important;
              border: 0 !important;
              padding: 0 !important;
              margin: 0 !important;
            }
            
            /* Your generated CSS */
          ${css}

          /* CMS Element Styles */
          .cms-content-container.grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
            gap: 16px;
          }
          .cms-content-container.cards {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
            gap: 24px;
          }
          .cms-content-container.list {
            display: flex;
            flex-direction: column;
            gap: 16px;
          }
          .cms-content-item {
            padding: 16px;
            background: #fff;
            border-radius: 4px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }
        </style>
        <!-- Custom head code -->
        ${customHeadCode}
    </head>
    <body>
        <div id="root">
            ${html}
        </div>
        <script>
            window.addEventListener('load', function() {
                const sliders = document.querySelectorAll('[data-slider-role="container"]');
                const arrows = document.querySelectorAll('[data-slider-role="arrow"]');
                
                // Add direct event listeners as a last resort
                arrows.forEach(arrow => {
                    arrow.onclick = function(e) {
                        e.preventDefault();
                    };
                });
            });
        </script>
        <script>${navigationJS}</script>
        <script>${formJS}</script>
        <script>${sliderJS}</script>
        <script>${cartJS}</script>
        <script>${checkoutJS}</script>
        <!-- Custom body code -->
        ${customBodyCode}
    </body>
    </html>
  `;

  return {
    isTemplate: false,
    indexHTML,
    css,
    customCss,
    js: `
      ${navigationJS}
      ${formJS}
      ${sliderJS}
      ${cartJS}
      ${checkoutJS}
    `
  };
};

export default generatePublishedBuild; 